import { type BoardState } from '@shared/board'
import { clsx } from 'clsx'
import { Crosshair, Link2, Square } from 'lucide-react'
import { memo, type ReactNode, useSyncExternalStore } from 'react'
import ReactMarkdown, { defaultUrlTransform, type ExtraProps } from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import type * as Y from 'yjs'
import { useBoardCtx } from '@/board/context'
import { useI18n } from '@/i18n/i18n'
import { labelOf as labelFor } from './describe'
import { useSessionTitle } from './session-title'
import { useFocusMention } from './focus'

import { Embed } from './Embed'
import { parseEmbed } from './embeds'
import { MENTION_PROTOCOLS, type Mention, parseMention, splitMentions } from './mentions'
import { parseMaterialHref } from '@shared/material'
import { MaterialChip, MaterialEmbed } from '@/materials/MaterialLink'

/** Keep our pointer schemes; everything else goes through the safe default. */
function urlTransform(url: string) {
  return MENTION_PROTOCOLS.some((p) => url.startsWith(p)) || url.startsWith('mat:')
    ? url
    : defaultUrlTransform(url)
}

/** A pointer at something on the board: its live title, click to look at it. */
export function MentionChip({ mention, fallback }: { mention: Mention; fallback: ReactNode }) {
  const { client } = useBoardCtx()
  const { t } = useI18n()
  const focus = useFocusMention()
  const snap = useSyncExternalStore(client.subscribe, client.getSnapshot)
  const sessionTitle = useSessionTitle()
  const s = snap.state
  const labelOf = (s2: BoardState, doc: Y.Doc, id: string) => labelFor(s2, doc, id, sessionTitle)
  let label: ReactNode = fallback
  let gone = false
  let Icon = Square
  if (mention.kind === 'item') {
    const live = labelOf(s, client.doc, mention.id)
    gone = live === null
    if (live) label = mention.quote ? `${live} «${mention.quote}»` : live
  } else if (mention.kind === 'edge') {
    const e = s.edges.get(mention.id)
    gone = !e
    Icon = Link2
    if (e) {
      const a = labelOf(s, client.doc, e.from) ?? '…'
      const b = labelOf(s, client.doc, e.to) ?? '…'
      label = `${a} → ${b}`
    }
  } else {
    Icon = Crosshair
    if (mention.kind === 'items') gone = mention.ids.every((id) => !s.items.has(id))
  }
  return (
    <button
      type="button"
      data-mention=""
      disabled={gone}
      title={gone ? t('mention.gone') : t('mention.show')}
      onClick={() => {
        void focus(mention)
      }}
      className={clsx(
        'mx-0.5 inline-flex max-w-[16rem] translate-y-[-1px] items-center gap-1 rounded-md px-1.5 align-middle text-[12.5px] leading-5 font-medium transition-colors',
        gone
          ? 'cursor-default bg-surface-2 text-muted line-through'
          : 'bg-ai-soft text-ai hover:bg-ai hover:text-white',
      )}
    >
      <Icon size={11} className="flex-none opacity-70" />
      <span className="truncate">{label}</span>
    </button>
  )
}

/**
 * Text shown as written (a person's chat message, a message on the board),
 * with its pointers at the board and into materials as chips.
 */
export function TextWithPointers({ text, chipClass }: { text: string; chipClass?: string }) {
  return splitMentions(text).map((part, i) =>
    part.kind === 'text' ? (
      <span key={i}>{part.text}</span>
    ) : (
      <span key={i} className={chipClass}>
        {part.kind === 'mention' ? (
          <MentionChip mention={part.mention} fallback={part.label || '…'} />
        ) : (
          <MaterialChip target={part.ref} fallback={part.label} />
        )}
      </span>
    ),
  )
}

/**
 * A message on the board: markdown as its author wrote it (a line break is a
 * line break), pointers as chips. An embed written by mistake shows as a chip:
 * embeds belong in the chat.
 */
export const NoteText = memo(function NoteText({
  text,
  chipClass,
}: {
  text: string
  chipClass?: string
}) {
  const chip = (href: string | undefined, fallback: ReactNode) => {
    const m = href ? (parseMention(href) ?? parseEmbed(href)) : null
    if (m)
      return (
        <span className={chipClass}>
          <MentionChip mention={m} fallback={fallback} />
        </span>
      )
    const place = href ? parseMaterialHref(href) : null
    if (place)
      return (
        <span className={chipClass}>
          <MaterialChip target={place} fallback={fallback} />
        </span>
      )
    return null
  }
  return (
    <div className="say-prose">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        urlTransform={urlTransform}
        components={{
          a: ({ href, children }) =>
            chip(href, children) ?? (
              <a href={href} target="_blank" rel="noreferrer noopener" className={chipClass}>
                {children}
              </a>
            ),
          img: ({ src, alt }) => chip(typeof src === 'string' ? src : undefined, alt || '…') ?? alt,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  )
})

/** Whether a markdown paragraph holds an embed (an image pointing at the board). */
function holdsEmbed(node: ExtraProps['node']): boolean {
  return !!node?.children.some(
    (c) =>
      c.type === 'element' &&
      c.tagName === 'img' &&
      typeof c.properties.src === 'string' &&
      (parseEmbed(c.properties.src) !== null || parseMaterialHref(c.properties.src) !== null),
  )
}

/**
 * What the AI said: markdown, with board pointers rendered as chips and board
 * images (`![…](item://…)`, `![](region://…)`) as embeds.
 */
export const SayText = memo(function SayText({
  text,
  since = null,
}: {
  text: string
  /** When the turn that said it ended (server clock), for embeds; null while it runs. */
  since?: number | null
}) {
  return (
    <div className="say-prose">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={urlTransform}
        components={{
          a: ({ href, children }) => {
            const m = href ? parseMention(href) : null
            if (m) return <MentionChip mention={m} fallback={children} />
            const place = href ? parseMaterialHref(href) : null
            if (place) return <MaterialChip target={place} fallback={children} />
            return (
              <a href={href} target="_blank" rel="noreferrer noopener">
                {children}
              </a>
            )
          },
          img: ({ src, alt }) => {
            const m = parseEmbed(typeof src === 'string' ? src : undefined)
            if (m) return <Embed mention={m} caption={alt ?? ''} since={since} />
            const place = typeof src === 'string' ? parseMaterialHref(src) : null
            if (place) return <MaterialEmbed target={place} caption={alt ?? ''} />
            return <img src={src} alt={alt ?? ''} className="max-w-full rounded-md" />
          },
          // An embed is a block: it cannot sit inside a <p>.
          p: ({ node, children }) => (holdsEmbed(node) ? <div>{children}</div> : <p>{children}</p>),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  )
})
